import React, { useState } from 'react'
import { useTranslation } from '@sailor/i18n-web'
import Selector from '@roo/roo/Selector'
import Button from '@roo/roo/Button'
import { createPolling, downloadFile } from '@/common/utils'
import { failToast } from '@/common/utils/toast'
import { fetchFundFlowsExport, queryFundFlowsExportStatus } from '../../../../utils/api'
import { IFundFlowsQueryParams } from '../../../../utils/types'
import styles from './index.module.less'

const TRANSACTION_TYPE_OPTIONS = [
  { value: '10', i18nKey: 'Keeta_EE_Payment_0y0B', fallback: '支付' },
  { value: '20', i18nKey: 'Keeta_EE_Refund_4JEr', fallback: '退款' },
  { value: '30', i18nKey: 'Keeta_EE_Recharge_9hE0', fallback: '增值' },
  { value: '70', i18nKey: 'Keeta_EE_Deduction_fCmx', fallback: '结算扣款' },
  { value: '80', i18nKey: 'Keeta_EE_reversal_deduction_SNjL', fallback: '结算扣款冲销' },
  { value: '90', i18nKey: 'Keeta_EE_reversal_recharge_Y8ck', fallback: '充值冲销' },
  { value: '40', i18nKey: 'Keeta_EE_Adjustments_billing_T4Kq', fallback: '帳單調整' },
  { value: '50', i18nKey: 'Keeta_EE_Cash_withdrawal_fqu7', fallback: '提现' },
  { value: '60', i18nKey: 'Keeta_EE_Adjustment_crediting_dU9a', fallback: '信用調整' },
]

export interface TransactionFilterProps {
  /** 当前选中的变动类型，空字符串表示全部 */
  selectedTypes: string[];
  /** 筛选变更回调 */
  onTypeChange: (types: string[]) => void;
  /** 当前表格查询参数，导出直接复用 */
  queryParams: IFundFlowsQueryParams;
}

/**
 * 资金变动明细筛选区域
 * 包含变动类型筛选选择器和导出按钮
 */
export const TransactionFilter: React.FC<TransactionFilterProps> = ({
  selectedTypes,
  onTypeChange,
  queryParams,
}) => {
  const { t } = useTranslation()
  const [exportLoading, setExportLoading] = useState(false)
  const options = TRANSACTION_TYPE_OPTIONS.map(item => ({
    value: item.value,
    label: t(item.i18nKey) || item.fallback,
  }))

  const handleChange = (val: string[]) => {
    onTypeChange(val?.length ? val : [])
  }

  const handleExport = async () => {
    if (exportLoading || !queryParams.startTime || !queryParams.endTime) return
    setExportLoading(true)
    try {
      const { pageNo: omitPageNo, pageSize: omitPageSize, ...exportParams } = queryParams
      const token = await fetchFundFlowsExport(exportParams)
      if (token) {
        createPolling({
          requestFunction: async () => queryFundFlowsExportStatus({ token }),
          checkSuccess: (response) => response.code === 30,
          onSuccess: (response) => {
            const { downLoadUrl } = response || {}
            const match = downLoadUrl?.match(/\/([^?#]+\.xlsx)(?:\?|#|$)/i)
            downloadFile({
              url: downLoadUrl,
              fileName: match?.[1],
            })
            setExportLoading(false)
          },
          checkError: (response) => response?.code === 40,
          onError: (error) => {
            failToast(error?.message || t('Keeta_EE_export_try_again_nDBU', '导出失败，请稍后重试！'))
            setExportLoading(false)
          },
        })
      } else {
        failToast(t('Keeta_EE_export_try_again_nDBU', '导出失败，请稍后重试！'))
        setExportLoading(false)
      }
    } catch {
      failToast(t('Keeta_EE_export_try_again_nDBU', '导出失败，请稍后重试！'))
      setExportLoading(false)
    }
  }

  return (
    <>
      {/* 筛选区 */}
      <div className={styles.filterArea}>
        <div className={styles.filterItem}>
          <label className={styles.filterLabel}>{t('Keeta_EE_changes_types_uyMf', '变动类型')}</label>
          <div className={styles.selectorWrap}>
            <Selector
              multiple
              clearable
              placeholder={t('e_pc_please_select', '请选择')}
              value={selectedTypes}
              options={options}
              onChange={handleChange}
            />
          </div>
        </div>
      </div>
      {/* 操作行：导出居右，与员工管理操作行风格一致 */}
      <div className={styles.actionBar}>
        <div />
        <Button
          className={styles.exportBtn}
          loading={exportLoading}
          onClick={handleExport}
        >
          {t('Keeta_EE_Export_rQVk', '导出')}
        </Button>
      </div>
    </>
  )
}
